import { useEffect, useState, useCallback } from "react";
import api, { fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  supplier_new: "Proveedor nuevo",
  invoice_review: "Factura pendiente",
  invoice_ocr_error: "Error OCR",
  loyverse_store_unmapped: "Tienda sin sucursal",
  bank_unmatched: "Movimiento sin conciliar",
  import_error: "Error de importación",
};

const PRIO = {
  high: { label: "Alta", cls: "text-red-700 border-red-200 bg-red-50" },
  medium: { label: "Media", cls: "text-amber-700 border-amber-200 bg-amber-50" },
  low: { label: "Baja", cls: "text-slate-600 border-slate-200 bg-slate-50" },
};

export default function Revisar() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/review?status=open").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (item, action) => {
    await api.post(`/review/${item.id}/resolve`, { action });
    toast.success(action === "discard" ? "Descartado" : "Resuelto");
    load();
  };

  const goTo = (item) => {
    if (item.type === "supplier_new") navigate("/proveedores");
    else if (item.type?.startsWith("invoice")) navigate("/facturas");
    else if (item.type === "loyverse_store_unmapped") navigate("/configuracion");
  };

  return (
    <div className="space-y-6" data-testid="revisar-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Bandeja de revisión</h1>
        <p className="text-sm text-slate-500 mt-1">Ante la duda, nada se decide automáticamente — todo pasa por aquí</p>
      </div>
      <div className="space-y-3">
        {items.map((item) => (
          <Card key={item.id} data-testid={`review-item-${item.id}`} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4 flex items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={PRIO[item.priority]?.cls}>{PRIO[item.priority]?.label || item.priority}</Badge>
                  <Badge variant="outline" className="text-slate-600">{TYPE_LABELS[item.type] || item.type}</Badge>
                  <span className="text-xs text-slate-400">{fmtDate(item.created_at)}</span>
                </div>
                <div className="font-medium text-sm mt-2">{item.title}</div>
                <div className="text-sm text-slate-500 mt-0.5">{item.description}</div>
                {item.suggested_action && (
                  <div className="text-xs text-slate-400 mt-1.5">Acción sugerida: {item.suggested_action}</div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button data-testid={`review-goto-${item.id}`} size="sm" variant="outline" className="gap-1" onClick={() => goTo(item)}>
                  Ir <ArrowRight className="w-3.5 h-3.5" />
                </Button>
                <Button data-testid={`review-resolve-${item.id}`} size="sm" variant="outline"
                  className="text-green-700 border-green-200 hover:bg-green-50 gap-1" onClick={() => act(item, "resolve")}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Resolver
                </Button>
                <Button data-testid={`review-discard-${item.id}`} size="sm" variant="outline"
                  className="text-slate-500 gap-1" onClick={() => act(item, "discard")}>
                  <XCircle className="w-3.5 h-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && (
          <Card><CardContent className="py-16 text-center">
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-3" />
            <div className="font-heading font-semibold">Todo en orden</div>
            <div className="text-sm text-slate-500 mt-1">No hay elementos pendientes de revisión</div>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
